import React, { useEffect, useState } from 'react';
import { Card, Divider, useIsMobile, Icon } from '@amzn/storm-ui';
import styled from 'styled-components';
import { Title } from './Title';
import { AccountSelector } from './AccountSelector';
import { useAccountContext } from './AccountContextProvider';
import { AccountListSearch } from './AccountListSearch';
import { useResizeObserver } from '../hooks/useResizeObserver';
import { InterstitialPageMetricsPublisher } from '../metrics';
import { spinner } from '@amzn/storm-ui-icons';
import { SecurityWarningModal } from './SecurityWarningModal';
import {CardContentTypeSelector} from "src/components/CardContentTypeSelector";

const AppWrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.gray[25]};
`;

const CardWrapperContainer = styled.div`
  max-width: 700px;
  min-width: 500px;
  margin: auto;
  padding: 44px 0px;
`;

const CardDivider = styled(Divider)`
  margin-top: 18px;
  margin-inline: -18px;
`;

const AccountSelectorCard = styled(Card)<{ cardHeight?: number }>`
  height: ${(props) => !!props.cardHeight && props.cardHeight > 0 ? `${props.cardHeight}px` : ``};
`;

const CardContainer = styled.div<{ cardContainerHeight?: number }>`
  display: flex;
  flex-direction: column;
  height: ${(props) => !!props.cardContainerHeight && props.cardContainerHeight > 0 ? `${props.cardContainerHeight}px` : ``};
`;

const CardContentWrapper = styled.div`
  overflow: hidden;
  display: flex;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const MobileWrapper = styled.div`
  padding-top: 34px;
  padding-bottom: 52px;
  padding-inline: 16px;
  background: ${({ theme }) => theme.palette.concrete};
`;

const accountListLengthForSearchBox = 10;
const cardTopAndBottomPadding = 88;
const cardInnerTopAndBottomPadding = 36;

export const App: React.VFC = () => {
  const {
    isLoading,
    accounts,
    hasNestedAccounts,
    preSelectedAccount,
    setPreSelectedAccount,
  } = useAccountContext();

  useEffect(() => {
    if (!hasNestedAccounts) {
      setPreSelectedAccount(undefined);
    }
  }, [hasNestedAccounts]);

  const isMobile = useIsMobile();
  const numOfAccounts = accounts.reduce((count, account) => count + account.accounts.length, 0);
  const shouldDisplaySearchBox = isMobile ? numOfAccounts > accountListLengthForSearchBox :
    numOfAccounts > accountListLengthForSearchBox && !preSelectedAccount;

  const [cardHeight, setCardHeight] = useState(0);
  const [cardContainerHeight, setCardContainerHeight] = useState(0);
  const chromeHeader = document.getElementById("ChromeHeaderContainer");
  const chromeFooter = document.getElementById("ChromeFooterContainer");

  const handleCardHeightChange = () => {
    const headerHeight = chromeHeader?.getBoundingClientRect().height || 0;
    const footerHeight = chromeFooter?.getBoundingClientRect().height || 0;
    // 44px top and bottom padding
    const fullCardHeight = window.innerHeight - footerHeight - headerHeight - cardTopAndBottomPadding;
    setCardHeight(fullCardHeight);
    // 18px top and bottom padding
    setCardContainerHeight(fullCardHeight - cardInnerTopAndBottomPadding);
  }

  useResizeObserver(handleCardHeightChange, chromeHeader);
  useResizeObserver(handleCardHeightChange, chromeFooter);

  useEffect(() => {
    InterstitialPageMetricsPublisher.publishCounterMonitor('success', 1);
    window.addEventListener('resize', handleCardHeightChange);

    return () => window.removeEventListener('resize', handleCardHeightChange);
  }, []);

  const CardSpinner = (
    <SpinnerWrapper>
      <Icon type={spinner} size="lg"></Icon>
    </SpinnerWrapper>
  );

  const MobileCardContent =
    <>
      <Title />
      {shouldDisplaySearchBox && (<AccountListSearch/>)}
      <AccountSelector/>
      {hasNestedAccounts && (<CardContentTypeSelector isMobile={true}/>)}
    </>

  if (isMobile) {
    return (
        <MobileWrapper>
             {isLoading ? CardSpinner : MobileCardContent}
        </MobileWrapper>
    );
  }

  const DesktopCardContent = (
    <>
      <Title />
      <CardDivider />
      {shouldDisplaySearchBox && (<AccountListSearch />)}
      <CardContentWrapper id="test">
        <AccountSelector/>
        {hasNestedAccounts && (<CardContentTypeSelector isMobile={false}/>)}
      </CardContentWrapper>
    </>
  );

  const isExternalMode = window.sessionStorage.getItem('ATC_EXTERNAL_TOGGLE') === "true";

  return (
    <AppWrapper>
      <SecurityWarningModal isOpen={isExternalMode} />
      <CardWrapperContainer>
        <AccountSelectorCard data-testid="interstitial-card" padding="medium" cardHeight={cardHeight}>
          <CardContainer cardContainerHeight={cardContainerHeight}>
              {isLoading ? CardSpinner : DesktopCardContent}
          </CardContainer>
        </AccountSelectorCard>
      </CardWrapperContainer>
    </AppWrapper>
  );
}
